import {ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output} from '@angular/core';
import {SharedIconsModule} from '../../../../shared-modules/shared-icons/shared-icons.module';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {ISharedToastButton, SharedToastColour, SharedToastPosition} from '../../../shared-models/toasts/shared-toast';

@Component({
  selector: 'app-shared-toast',
  templateUrl: './shared-toast.component.html',
  styleUrls: ['./shared-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedIconsModule],
})
export class SharedToastComponent {

  @Input() header = '';
  @Input() message = '';
  @Input() colour: SharedToastColour = 'primary';
  @Input() position: SharedToastPosition = 'bottom';
  @Input() buttons: ISharedToastButton[] = [];
  @Input() cssClass: string | string[] | undefined;

  @Output() readonly dismissed = new EventEmitter<string | undefined>();

  @HostBinding('attr.role')
  readonly statusRole = 'status';

  @HostBinding('attr.aria-live')
  readonly politeLiveRegion = 'polite';

  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;

  @HostBinding('class.shared-toast-position-top')
  get isTopPosition(): boolean {
    return this.position === 'top';
  }

  @HostBinding('class.shared-toast-position-middle')
  get isMiddlePosition(): boolean {
    return this.position === 'middle';
  }

  @HostBinding('class.shared-toast-position-bottom')
  get isBottomPosition(): boolean {
    return this.position === 'bottom';
  }

  protected get toastCardClass(): string {
    const extra = Array.isArray(this.cssClass) ? this.cssClass.join(' ') : (this.cssClass ?? '');
    return `shared-toast shared-toast-${this.colour} ${extra}`.trim();
  }

  protected get toastIcon(): IconsEnum {
    if (this.colour === 'success') {
      return this.icons.checkCircle;
    }
    if (this.colour === 'danger') {
      return this.icons.alertBox;
    }
    return this.icons.infoSlabCircleOutline;
  }

  protected get toastIconColour(): IconColourEnum {
    if (this.colour === 'success') {
      return this.iconColourEnum.green;
    }
    if (this.colour === 'danger') {
      return this.iconColourEnum.red;
    }
    if (this.colour === 'warning') {
      return this.iconColourEnum.yellow;
    }
    return this.iconColourEnum.primary;
  }

  /**
   * Runs an optional toast action and then dismisses the toast.
   */
  protected onButtonClick(button: ISharedToastButton): void {
    button.handler?.();
    this.dismissed.emit(button.role ?? button.text);
  }
}
